import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { IonButton, IonIcon, IonSpinner } from '@ionic/react';
import { Helmet } from 'react-helmet-async';
import { checkmarkCircleOutline, closeCircleOutline } from 'ionicons/icons';
import api, { getAuthToken } from '../../services/api';
import './InvitePage.css';

const InvitePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line no-use-before-define
    fetchInvitation();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchInvitation = async () => {
    try {
      const authToken = getAuthToken();
      
      if (!authToken) {
        setError('Please sign in first to accept this invitation');
        setLoading(false);
        return;
      }

      const data = await api.get(`/invitations/${token}`);
      setInvitation(data.invitation);
    } catch (error) {
      console.error('Error fetching invitation:', error);
      setError(error.message || 'Failed to load invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvitation = async () => {
    setAccepting(true);
    setError('');

    try {
      const data = await api.post(`/invitations/${token}/accept`, {});
      setSuccess(true);
      
      setTimeout(() => {
        navigate(`/projects/${data.projectId}`);
      }, 2000);
    } catch (error) {
      console.error('Error accepting invitation:', error);
      setError(error.message || 'Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  const handleDecline = () => {
    navigate('/projects');
  };

  const isAuthenticated = () => {
    return !!getAuthToken();
  };

  if (loading) {
    return (
      <div className="invite-page">
        <div className="invite-container">
          <IonSpinner name="crescent" className="spinner" />
          <p>Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated()) {
    return (
      <div className="invite-page">
        <div className="invite-container">
          <IonIcon icon={closeCircleOutline} className="icon error-icon" />
          <h1>Sign In Required</h1>
          <p className="message">Please sign in with your account to accept this invitation.</p>
          <IonButton onClick={() => navigate('/auth/login')} color="primary">
            Sign In
          </IonButton>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="invite-page">
        <div className="invite-container">
          <IonIcon icon={checkmarkCircleOutline} className="icon success-icon" />
          <h1>Invitation Accepted!</h1>
          <p className="message">You now have access to this project. Redirecting...</p>
          <IonSpinner name="dots" className="spinner" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="invite-page">
        <div className="invite-container">
          <IonIcon icon={closeCircleOutline} className="icon error-icon" />
          <h1>Unable to Accept Invitation</h1>
          <p className="message error">{error}</p>
          <IonButton onClick={() => navigate('/projects')} color="primary">
            Go to Projects
          </IonButton>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Project Invitation - Teamy</title>
        <meta name="description" content="Accept a project invitation to join a Teamy project." />
      </Helmet>
      <div className="invite-page">
        <div className="invite-container">
          <div className="invite-card">
            <h1>Project Invitation</h1>
          
          <div className="invitation-details">
            <div className="detail-item">
              <span className="label">Project:</span>
              <span className="value">{invitation.projectName}</span>
            </div>
            <div className="detail-item">
              <span className="label">Invited by:</span>
              <span className="value">{invitation.invitedBy}</span>
            </div>
            <div className="detail-item">
              <span className="label">Your email:</span>
              <span className="value">{invitation.email}</span>
            </div>
          </div>

          <p className="invitation-message">
            You've been invited to collaborate on this project. 
            Accepting will give you access to view and run tests for this project.
          </p>

          <div className="action-buttons">
            <IonButton
              onClick={handleDecline}
              fill="outline"
              color="medium"
              disabled={accepting}
            >
              Decline
            </IonButton>
            <IonButton
              onClick={handleAcceptInvitation}
              color="primary"
              disabled={accepting}
            >
              {accepting ? 'Accepting...' : 'Accept Invitation'}
            </IonButton>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default InvitePage;

